// ============================================================================
// BREAK-GLASS CONTEXT
// React bindings for services/breakGlass.ts: re-renders on grant changes, ends
// grants on navigation away, logout and expiry, and exposes per-scope hooks.
// ============================================================================

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  BreakGlassContext,
  BreakGlassDenial,
  BreakGlassGrant,
  BreakGlassScope,
  GrantOptions,
  clearAllBreakGlass,
  endBreakGlass,
  getActiveGrant,
  getEffectiveGrant,
  grantBreakGlass,
  nextExpiry,
  pruneExpired,
  pruneForPath,
  rememberSessionProjects,
  scopeKey,
  subscribeBreakGlass,
  subscribeBreakGlassDenied,
} from '../services/breakGlass';

let version = 0;
const subscribeVersion = (fn: () => void) => subscribeBreakGlass(() => { version++; fn(); });
const getVersion = () => version;

export const BreakGlassProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const v = useSyncExternalStore(subscribeVersion, getVersion);

  useEffect(() => { pruneForPath(pathname); }, [pathname]);

  useEffect(() => { if (!user) clearAllBreakGlass(); }, [user]);

  useEffect(() => {
    const at = nextExpiry();
    if (at === null) return;
    const t = setTimeout(() => pruneExpired(), Math.max(0, at - Date.now()) + 50);
    return () => clearTimeout(t);
  }, [v]);

  return <>{children}</>;
};

export interface UseBreakGlassResult {
  grant: BreakGlassGrant | null;
  active: boolean;
  start: (ctx: BreakGlassContext, opts?: GrantOptions) => BreakGlassGrant;
  end: () => void;
}

export function useBreakGlass(scope: BreakGlassScope | null): UseBreakGlassResult {
  useSyncExternalStore(subscribeVersion, getVersion);
  // A session also counts as granted under an active grant for its project.
  const grant = scope ? getEffectiveGrant(scope) : null;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const start = useCallback((ctx: BreakGlassContext, opts?: GrantOptions) => {
    if (!scopeRef.current) throw new Error('No break-glass scope');
    return grantBreakGlass(scopeRef.current, ctx, opts);
  }, []);
  const end = useCallback(() => {
    const s = scopeRef.current;
    if (!s) return;
    const g = getActiveGrant(s) ? null : getEffectiveGrant(s);
    endBreakGlass(g ? g.scope : s);
  }, []);
  return { grant, active: !!grant, start, end };
}

// Tracks whether a 403 that break-glass could resolve has been seen for this scope.
export function useBreakGlassDenied(scope: BreakGlassScope | null): [BreakGlassDenial | null, () => void] {
  const [denial, setDenial] = useState<BreakGlassDenial | null>(null);
  const key = scope ? scopeKey(scope) : '';
  // Layout effect so the listener is in place before children's load effects run.
  useLayoutEffect(() => {
    setDenial(null);
    if (!key) return;
    return subscribeBreakGlassDenied(d => { if (scopeKey(d.scope) === key) setDenial(d); });
  }, [key]);
  const reset = useCallback(() => setDenial(null), []);
  return [denial, reset];
}

// May use break-glass at all (System Administrator holders, from /me).
export function useIsSystemAdmin(): boolean {
  const { user } = useAuth();
  return !!user?.breakGlassEligible;
}

// Opens a session from its project's session list. Recording the session's project
// before navigating lets an active project grant cover the session page's first
// requests, rather than waiting for the session list to be refetched.
export function useOpenProjectSession(projectId: string | undefined): (sessionId: string) => void {
  const navigate = useNavigate();
  return useCallback((sessionId: string) => {
    if (projectId) rememberSessionProjects(projectId, [sessionId]);
    navigate(`/session/${sessionId}`);
  }, [projectId, navigate]);
}
